import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, Sparkles, Milestone, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLE = {
  completed: { icon: CheckCircle2, className: "text-emerald-400" },
  in_progress: { icon: Circle, className: "text-amber-400" },
  not_started: { icon: Circle, className: "text-slate-600" },
};

export default function RoadmapTab() {
  const [data, setData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = (force = false) => api.get(`/career/roadmap${force ? "?force=true" : ""}`).then((r) => setData(r.data));

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try { await load(true); } finally { setRegenerating(false); }
  };

  if (!data) return <div className="flex justify-center py-16" data-testid="roadmap-tab-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="roadmap-tab">
      <div className="glass-panel rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/10 to-transparent" />
        <div className="relative flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-400">AI Roadmap</span>
          </div>
          <button onClick={regenerate} disabled={regenerating} data-testid="roadmap-regenerate-btn" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed relative">{data.overview}</p>
        {data.next_step_advice && (
          <p className="text-sm text-indigo-300 mt-3 font-medium relative">→ {data.next_step_advice}</p>
        )}
      </div>

      <div className="relative pl-6" data-testid="roadmap-steps">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" />
        {data.steps.map((s) => {
          const style = STATUS_STYLE[s.status];
          const Icon = style.icon;
          return (
            <Link key={s.cert_id} to={`/certifications/${s.cert_id}`} data-testid={`roadmap-step-${s.cert_id}`} className="relative flex items-start gap-4 pb-6 group">
              <Icon className={`w-4 h-4 absolute -left-6 top-1 ${style.className} bg-background rounded-full`} />
              <div className="flex-1 glass-panel rounded-xl p-4 group-hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{s.name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: s.color, background: `${s.color}1a` }}>{s.level}</span>
                </div>
                {data.step_notes?.[s.cert_id] && <p className="text-xs text-slate-400 mt-1.5">{data.step_notes[s.cert_id]}</p>}
                {s.best_exam_score !== null && <p className="text-xs text-emerald-400 mt-1.5 font-semibold">Best score: {s.best_exam_score}%</p>}
              </div>
            </Link>
          );
        })}
        {data.future_milestones.map((m) => (
          <div key={m.name} className="relative flex items-start gap-4 pb-6 opacity-50" data-testid={`roadmap-future-${m.name}`}>
            <Milestone className="w-4 h-4 absolute -left-6 top-1 text-slate-600 bg-background rounded-full" />
            <div className="flex-1 border border-dashed border-white/10 rounded-xl p-4">
              <span className="text-sm font-semibold text-slate-400">{m.name}</span>
              <p className="text-xs text-slate-500 mt-1.5">{m.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
